import React, { useState } from 'react';

// components
import ChartFilter from 'components/charts/chartFilter/chartFilter';
import ChartInfoButton from 'components/charts/chartInfoButton/chartInfoButton';
import ChartResetButton from 'components/charts/chartResetButton/chartResetButton';
import ChartTooltip from 'components/charts/chartTooltip/chartTooltip';
import DatasetCombo from 'components/charts/datasetCombo/datasetCombo';
import IWPSlider from 'components/charts/slider/slider';
import { ChartComboItem } from 'components/charts/chartCombo/chartCombo';
import LineChartNew from 'components/charts/lineChartNew/lineChartNew';
import ChartActionsMobile from 'components/charts/chartActionsMobile/chartActionsMobile';

//utils
import chartsUtils, { DataCode, Dataset } from 'utils/charts.utils';

// styles
import './chart2.scss';
import { isArray } from 'lodash';
import ChartLegend from 'components/charts/chartLegend/chartLegend';
import DataMethodLink from 'components/charts/dataMethodLink/dataMethodLink';

const Chart2 = () => {
  const [chartRef, setChartRef] = useState<any>();
  const [dataset, setDataset] = useState<Dataset>(chartsUtils.getDatasets()[1]);
  const [dataCode, setDataCode] = useState<DataCode>(DataCode.realVorSteuern);
  const [yearRange, setYearRange] = useState<Array<number>>([
    chartsUtils.getMinYear(dataset, DataCode.realVorSteuern),
    chartsUtils.getMaxYear(dataset, DataCode.realVorSteuern),
  ]);
  const [selectedLocations, setSelectedLocations] = useState<Array<string>>(['ch']);
  const [noData, setNoData] = useState<boolean>(false);
  const [tooltipShow, setTooltipShow] = useState<boolean>(true);

  const getDatasetChartComboItem = () => {
    return {
      text: dataset.name,
      value: dataset.code,
    } as ChartComboItem;
  };

  const handleDatasetChange = (value: ChartComboItem) => {
    setDataset({
      code: value.value,
      name: value.text,
    });
  };

  const handleFilterClick = (dataCode: DataCode) => {
    setNoData(false);
    setDataCode(dataCode);
    setYearRange([chartsUtils.getMinYear(dataset, dataCode), chartsUtils.getMaxYear(dataset, dataCode)]);
  };

  const handleChangeYear = (value: number | number[]) => {
    if (isArray(value)) {
      setYearRange(value);
    }
  };

  const handleReset = () => {
    setSelectedLocations(['ch']);
    setDataCode(DataCode.realVorSteuern);
    setYearRange([chartsUtils.getMinYear(dataset, DataCode.realVorSteuern), chartsUtils.getMaxYear(dataset, DataCode.realVorSteuern)]);
  };

  const handleNoData = () => {
    setNoData(true);
    //setYear((year) => getNoDataNextYear(year));
  };

  const handleCallback = (chartRef: any) => {
    setChartRef(chartRef);
  };

  const handleSelectedLocation = (name: string) => {
    setSelectedLocations((selectedLocations) => [...selectedLocations, name]);
  };
  const handleUnSelectedLocation = (name: string) => {
    setSelectedLocations((selectedLocations) => selectedLocations.filter((s) => s !== name));
  };

  const handleExport = () => {
    if (chartRef) {
      chartRef.current.chart.exportChartLocal();
    }
  };

  const handleTooltipClose = () => {
    setTooltipShow(false);
  };

  return (
    <div className="chart2">
      <div className="grid-row chartfilters-wrapper">
        <div className="grid-cell xs-5 md-auto">
          <DatasetCombo onChange={handleDatasetChange} value={getDatasetChartComboItem()}></DatasetCombo>
        </div>
        <div className="grid-cell xs-7 md-auto">
          <ChartFilter
            label="Einkommensschwelle real"
            options={[DataCode.realVorSteuern, DataCode.realNachSteuern]}
            value={dataCode}
            onClick={handleFilterClick}
          />
        </div>
        <div className="grid-cell xs-12 md-grow chartfilters-slider-container">
          <IWPSlider
            range={true}
            min={chartsUtils.getMinYear(dataset, dataCode)}
            max={chartsUtils.getMaxYear(dataset, dataCode)}
            onChange={handleChangeYear}
            value={yearRange}
          />
          <ChartTooltip
            show={noData}
            text={`Sie haben ein Jahr ausgewählt, für das keine Daten verfügbar sind. Ihnen wird das nächsthöhere Jahr angezeigt. Bitte beachten Sie, dass vor ${yearRange[0]} die Steuergrundlage nur zweijährlich bemessen wurde und die Daten dadurch auch nur zweijährlich verfügbar sind.`}
          />
        </div>
        <div className="grid-cell md-auto chartfilters-actions">
          <div className="grid-row">
            <div className="grid-cell">
              <ChartResetButton onClick={handleReset} />
            </div>
            <div className="grid-cell">
              <ChartInfoButton text="Geben Sie an, ob Sie sich für den Einkommensanteil vor oder nach Steuern interessieren und wählen Sie rechts aus, welches Jahr Sie sich anschauen möchten. Wenn Sie die generierte Grafik herunterladen möchten, klicken Sie bitte auf das Pfeilsymbol links." />
            </div>
          </div>
        </div>
      </div>
      <div className="chart2-wrapper">
        <div className="chart2-tooltip-wrapper">
          <div className={`chart2-tooltip ${tooltipShow ? 'show' : ''}`}>
            <div className="chart2-tooltip-text">
              Ab 2013 andere Datengrundlage der Steuerstatistiken
              <br />
              (siehe <DataMethodLink onClick={handleTooltipClose} />
              ).
            </div>
            <a href="javascript:void(0);" onClick={handleTooltipClose} className="chart2-tooltip-close"></a>
          </div>
        </div>
        <LineChartNew
          dataCode={dataCode}
          dataset={dataset}
          yearRange={yearRange}
          title="Top {DATASET} Einkommensschwelle real {FILTER} {YEAR_START} - {YEAR_END}"
          selectedLocations={selectedLocations}
          onNoData={handleNoData}
          onCallback={handleCallback}
          options={{
            tooltip: {
              headerLabel1: 'JAHR',
              headerLabel2: 'Einkommensschwelle real (in CHF)',
            },
            xAxis: {
              plotLines: [
                {
                  color: '#666666',
                  width: 1,
                  value: 2012,
                  zIndex: 99999,
                },
              ],
            },
            yAxis: {
              labels: {
                format: '{value:,.0f}',
              },
              title: 'Einkommensschwelle real (in Franken)',
              maximums: [
                { dataset: '20', value: 90000 },
                { dataset: '10', value: 120000 },
                { dataset: '5', value: 340000 },
                { dataset: '1', value: 340000 },
                { dataset: '0.1', value: 1200000 },
              ],
            },
          }}
          onSelectedLocation={handleSelectedLocation}
          onUnSelectedLocation={handleUnSelectedLocation}
          roundValues={true}
          pointFormat=""
        />
      </div>
      <ChartActionsMobile onExport={handleExport} onReset={handleReset}></ChartActionsMobile>
      <ChartLegend onExport={handleExport} />
    </div>
  );
};

export default Chart2;
